import React, {useMemo} from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  Button,
  BodySmall,
  BodyMedium,
  CircularLoader,
  Card,
  SemanticIcon,
  Icon,
  EmptyState,
  ErrorMessage,
} from '@spaced-out/ui-design-system';
import {
  formatConversationLastModifiedAt,
  useConversationListQuery,
} from 'src/utils/ask-ai';
import css from './past-conversations-view.css';


const RECENT_LIMIT = 10;

export interface PastConversationsViewProps {
  onSelectConversation: (id: string) => void;
  onSeeAll?: () => void;
}

export function PastConversationsView({
  onSelectConversation,
  onSeeAll,
}: PastConversationsViewProps) {
  const {t} = useLabelTranslation();
  const {data, isLoading, error, refetch} = useConversationListQuery();

  const recentConversations = useMemo(() => {
    const list = data?.conversations ?? [];
    return list.slice(0, RECENT_LIMIT);
  }, [data]);

  if (isLoading) {
    return (
      <div className={css.container}>
        <CircularLoader className={css.loader} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.container}>
        <ErrorMessage
          heading={`${t('Oops', 'Oops')}!`}
          description={`${t(
            'Failed_to_load_conversations',
            'Failed to load conversations',
          )}.`}
          imageVariant="serverError"
        >
          <Button onClick={refetch}>{t('Try_again', 'Try again')}</Button>
        </ErrorMessage>
      </div>
    );
  }

  if (recentConversations.length === 0) {
    return (
      <div className={css.container}>
        <EmptyState
          title={t('No_Past_Conversations', 'No Past Conversations')}
          testId="empty-state-story"
          imageVariant="data"
        />
      </div>
    );
  }

  return (
    <div className={css.container}>
      <ul className={css.list} role="list">
        {recentConversations.map((conv) => (
          <li key={conv.id} className={css.listItem}>
            <Card
              classNames={{wrapper: css.itemCard}}
              paddingBottom="small"
              paddingTop="small"
              paddingLeft="small"
              paddingRight="small"
              onClick={() => onSelectConversation(conv.id)}
              aria-label={conv.name ?? conv.id}
            >
              <SemanticIcon
                name="comments"
                type="solid"
                semantic="information"
              />

              <span className={css.itemContent}>
                <BodyMedium>
                  {conv.name ||
                    t('Untitled_conversation', 'Untitled conversation')}
                </BodyMedium>
                <BodySmall color="secondary">
                  {formatConversationLastModifiedAt(conv.last_modified_at)}
                </BodySmall>
              </span>
              <Icon
                size="small"
                className={css.itemArrow}
                name="chevron-right"
              />
            </Card>
          </li>
        ))}
      </ul>

      <div className={css.seeAllWrap}>
        <Button type="secondary" size="small" onClick={onSeeAll}>
          {t('See_All', 'See All')}
        </Button>
      </div>
    </div>
  );
}
